/**
 * The two sign-in lifetime settings (doc 28 §4.10) at one level — tenant,
 * device group or VPN user — with "inherit" as the first choice, and the value
 * that will actually apply (and where it comes from) underneath.
 *
 *   - Re-authentication interval: how long after the user last typed a
 *     password (or completed SSO) the gateway asks them to sign in again.
 *     1 h – 365 days; presets 10 h, 24 h, 1 week, 30 days, 90 days, or a
 *     custom number of hours.
 *   - Access-token lifetime: 5 – 60 min.
 *
 * Controlled: `value` is this level's own setting (null = inherit), in
 * seconds; `inherited` is what applies when the level sets nothing.
 */
import { useState } from 'react';
import type { EffectiveSignIn, SignInLevel, SignInPolicyLimits, SignInSource } from '../../services/access.service';
import { TOKEN_LIFETIME_PRESETS, describeValue, effectiveAt, formatDuration } from '../../utils/signInPolicy';

interface Props {
  value: SignInLevel;
  onChange: (next: SignInLevel) => void;
  /** What applies when this level sets nothing. */
  inherited: EffectiveSignIn;
  /** The source name a value set here is reported as. */
  level: SignInSource;
  limits: SignInPolicyLimits;
  /** Label of the inherit option, e.g. "Tenant value". */
  inheritLabel: string;
  disabled?: boolean;
  testIdPrefix: string;
}

const SignInLifetimeFields = ({ value, onChange, inherited, level, limits, inheritLabel, disabled, testIdPrefix }: Props) => {
  const presets = limits.presets.reauthInterval;
  const [customReauth, setCustomReauth] = useState(
    value.reauthInterval !== null && !presets.includes(value.reauthInterval),
  );
  const [customLifetime, setCustomLifetime] = useState(
    value.tokenLifetime !== null && !TOKEN_LIFETIME_PRESETS.includes(value.tokenLifetime),
  );

  const reauthSelect = value.reauthInterval === null ? 'inherit' : customReauth ? 'custom' : String(value.reauthInterval);
  const lifetimeSelect = value.tokenLifetime === null ? 'inherit' : customLifetime ? 'custom' : String(value.tokenLifetime);
  const effective = effectiveAt(value, inherited, level);

  const onReauthSelect = (v: string) => {
    if (v === 'inherit') {
      setCustomReauth(false);
      onChange({ ...value, reauthInterval: null });
    } else if (v === 'custom') {
      setCustomReauth(true);
      onChange({ ...value, reauthInterval: value.reauthInterval ?? inherited.reauthInterval });
    } else {
      setCustomReauth(false);
      onChange({ ...value, reauthInterval: Number(v) });
    }
  };

  const onLifetimeSelect = (v: string) => {
    if (v === 'inherit') {
      setCustomLifetime(false);
      onChange({ ...value, tokenLifetime: null });
    } else if (v === 'custom') {
      setCustomLifetime(true);
      onChange({ ...value, tokenLifetime: value.tokenLifetime ?? inherited.tokenLifetime });
    } else {
      setCustomLifetime(false);
      onChange({ ...value, tokenLifetime: Number(v) });
    }
  };

  // Custom inputs edit whole hours / minutes; an empty box reads as 0, which
  // the range check refuses before anything is saved.
  const hours = value.reauthInterval !== null ? Math.round(value.reauthInterval / 3600) : '';
  const minutes = value.tokenLifetime !== null ? Math.round(value.tokenLifetime / 60) : '';

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-sign-in`}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Re-authentication interval</label>
          <div className="flex gap-2">
            <select
              className="input-base w-full"
              value={reauthSelect}
              disabled={disabled}
              onChange={(e) => onReauthSelect(e.target.value)}
              data-testid={`${testIdPrefix}-reauth`}
            >
              <option value="inherit">{inheritLabel} — {describeValue(inherited.reauthInterval, inherited.reauthIntervalSource)}</option>
              {presets.map((p) => (
                <option key={p} value={String(p)}>{formatDuration(p)}</option>
              ))}
              <option value="custom">Custom…</option>
            </select>
            {reauthSelect === 'custom' && (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={Math.ceil(limits.ranges.reauthInterval.min / 3600)}
                  max={Math.floor(limits.ranges.reauthInterval.max / 3600)}
                  className="input-base w-24"
                  value={hours}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...value, reauthInterval: Math.round(Number(e.target.value) || 0) * 3600 })}
                  data-testid={`${testIdPrefix}-reauth-hours`}
                />
                <span className="text-xs text-gray-500">h</span>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">How long after a password (or SSO) sign-in the user must sign in again. 1 h – 365 days.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Access-token lifetime</label>
          <div className="flex gap-2">
            <select
              className="input-base w-full"
              value={lifetimeSelect}
              disabled={disabled}
              onChange={(e) => onLifetimeSelect(e.target.value)}
              data-testid={`${testIdPrefix}-lifetime`}
            >
              <option value="inherit">{inheritLabel} — {describeValue(inherited.tokenLifetime, inherited.tokenLifetimeSource)}</option>
              {TOKEN_LIFETIME_PRESETS.map((p) => (
                <option key={p} value={String(p)}>{formatDuration(p)}</option>
              ))}
              <option value="custom">Custom…</option>
            </select>
            {lifetimeSelect === 'custom' && (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={Math.ceil(limits.ranges.tokenLifetime.min / 60)}
                  max={Math.floor(limits.ranges.tokenLifetime.max / 60)}
                  className="input-base w-20"
                  value={minutes}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...value, tokenLifetime: Math.round(Number(e.target.value) || 0) * 60 })}
                  data-testid={`${testIdPrefix}-lifetime-minutes`}
                />
                <span className="text-xs text-gray-500">min</span>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">How often the app refreshes its token, and how stale an identity can get. 5 – 60 min.</p>
        </div>
      </div>
      <p className="text-xs text-gray-700" data-testid={`${testIdPrefix}-effective`}>
        Applies: sign in again every <span className="font-medium">{describeValue(effective.reauthInterval, effective.reauthIntervalSource)}</span>
        {' · '}token lifetime <span className="font-medium">{describeValue(effective.tokenLifetime, effective.tokenLifetimeSource)}</span>
      </p>
    </div>
  );
};

export default SignInLifetimeFields;
